import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  IconButton,
  Checkbox,
} from "@mui/material";
import FolderSharedIcon from "@mui/icons-material/FolderShared";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { NeoButton } from "../../shared/components";
import ScheduleDelaySection, { ScheduleDelayOptions } from "./ScheduleDelaySection";

export type DefaultSite = { name: string; role: string; checked: boolean };

export interface AddToSharePointSitesConfig extends ScheduleDelayOptions {
  defaultSites: DefaultSite[];
  technician_instruction: string;
}

const DEFAULT_CONFIG: AddToSharePointSitesConfig = {
  defaultSites: [
    { name: "", role: "read", checked: false },
    { name: "", role: "read", checked: false },
    { name: "", role: "read", checked: false },
  ],
  technician_instruction: "",
};

const ROLES = [
  { value: "read", label: "Read" },
  { value: "write", label: "Write" },
];

interface AddToSharePointSitesModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (config: AddToSharePointSitesConfig) => void;
  initialConfig?: AddToSharePointSitesConfig | null;
  schedulerEnabled?: boolean;
}

const AddToSharePointSitesModal: React.FC<AddToSharePointSitesModalProps> = ({
  open,
  onClose,
  onSave,
  initialConfig,
  schedulerEnabled = false,
}) => {
  const [config, setConfig] = useState<AddToSharePointSitesConfig>(DEFAULT_CONFIG);

  useEffect(() => {
    if (open) {
      setConfig(initialConfig || DEFAULT_CONFIG);
    }
  }, [open, initialConfig]);

  const handleDefaultSiteToggle = (index: number, checked: boolean) => {
    setConfig((prev) => ({
      ...prev,
      defaultSites: prev.defaultSites.map((s, i) =>
        i === index ? { ...s, checked } : s
      ),
    }));
  };

  const handleDefaultSiteChange = (
    index: number,
    field: "name" | "role",
    value: string
  ) => {
    setConfig((prev) => ({
      ...prev,
      defaultSites: prev.defaultSites.map((s, i) =>
        i === index ? { ...s, [field]: value } : s
      ),
    }));
  };

  const handleAddDefaultSite = () => {
    setConfig((prev) => ({
      ...prev,
      defaultSites: [...prev.defaultSites, { name: "", role: "read", checked: true }],
    }));
  };

  const handleRemoveDefaultSite = (index: number) => {
    setConfig((prev) => ({
      ...prev,
      defaultSites: prev.defaultSites.filter((_, i) => i !== index),
    }));
  };

  const handleScheduleDelayChange = (key: keyof ScheduleDelayOptions, value: string) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    onSave(config);
    onClose();
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": { "&.Mui-focused fieldset": { borderColor: "#705D4A" } },
  };

  const radioSx = { "&.Mui-checked": { color: "#705D4A" } };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth disableEnforceFocus disableScrollLock>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontFamily: "var(--theo-font-sans)",
          fontWeight: 600,
        }}
      >
        <FolderSharedIcon sx={{ color: "#0078D4" }} />
        Add User to SharePoint Sites
      </DialogTitle>
      <DialogContent
        sx={{
          fontFamily: "var(--theo-font-sans)",
          "& .MuiInputLabel-root": { fontFamily: "var(--theo-font-sans)" },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <ScheduleDelaySection
            enabled={schedulerEnabled}
            options={config}
            onChange={handleScheduleDelayChange}
          />

          {/* Default Sites */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Default Sites
            </Typography>
            {config.defaultSites.map((s, i) => (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 0.5,
                  flexWrap: "wrap",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={s.checked}
                      onChange={(e) => handleDefaultSiteToggle(i, e.target.checked)}
                      sx={{ "&.Mui-checked": { color: "#705D4A" } }}
                    />
                  }
                  label=""
                />
                <TextField
                  size="small"
                  placeholder="Site name or URL"
                  value={s.name}
                  onChange={(e) => handleDefaultSiteChange(i, "name", e.target.value)}
                  sx={{ flex: "1 1 180px", minWidth: 180, ...textFieldSx }}
                  inputProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
                />
                <RadioGroup
                  row
                  value={s.role}
                  onChange={(e) => handleDefaultSiteChange(i, "role", e.target.value)}
                  sx={{ "& .MuiFormControlLabel-label": { fontFamily: "var(--theo-font-sans)", fontSize: "0.875rem" } }}
                >
                  {ROLES.map((r) => (
                    <FormControlLabel key={r.value} value={r.value} control={<Radio size="small" sx={radioSx} />} label={r.label} />
                  ))}
                </RadioGroup>
                {config.defaultSites.length > 1 && (
                  <IconButton size="small" onClick={() => handleRemoveDefaultSite(i)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddDefaultSite}
              sx={{ fontFamily: "var(--theo-font-sans)", color: "#705D4A", textTransform: "none", mt: 0.5 }}
            >
              Add site
            </Button>
          </Box>

          {/* Technician instruction (for LLM) */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Instruction for Theo AI
            </Typography>
            <TextField
              multiline
              minRows={3}
              size="small"
              placeholder="e.g. If the ticket provides a department path, resolve which SharePoint site matches and confirm the site URL before granting access."
              value={config.technician_instruction}
              onChange={(e) =>
                setConfig((prev) => ({
                  ...prev,
                  technician_instruction: e.target.value,
                }))
              }
              sx={{ width: "100%", ...textFieldSx }}
              inputProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
              InputLabelProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
            />
          </Box>

        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ fontFamily: "var(--theo-font-sans)", color: "#666" }}>
          Cancel
        </Button>
        <NeoButton variant="neo" onClick={handleSave}>
          Save
        </NeoButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddToSharePointSitesModal;
